import { useEffect, useMemo, useState } from "react";
import { workspaceSnapshot as fallbackWorkspaceSnapshot } from "../shared/fixtures/workspaceFixture.js";
import type {
  AdminWorkspaceSnapshot,
  Role,
  StudentWorkspaceSnapshot,
  TeacherWorkspaceSnapshot
} from "../shared/domain/index.js";

export type Workspace = "student" | "teacher" | "admin";
export type ShellView = "landing" | "login" | "app";
export type AppRoute = "dashboard" | "student_profile";

export interface AppSession {
  role: Role;
  userId: string;
  institutionId: string | null;
  displayName: string;
  email: string;
  workspace: Workspace;
}

function initialShellView(): ShellView {
  const pathname = window.location.pathname;
  if (pathname === "/login" || pathname === "/forgot-password" || pathname === "/reset-password" || pathname === "/register") return "login";
  if (pathname === "/dashboard" || pathname.startsWith("/app")) return "app";
  if (pathname === "/student/profile") return "app";
  return "landing";
}

function setPath(path: string) {
  window.history.pushState({}, "", path);
}

function availableWorkspaces(session: AppSession): Workspace[] {
  if (session.workspace === "admin") return ["admin"];
  return [session.workspace];
}

export function useSession() {
  const [session, setSession] = useState<AppSession | null>(null);
  const [shellView, setShellView] = useState<ShellView>(() => initialShellView());
  const [workspace, setWorkspace] = useState<Workspace>("student");
  const [appRoute, setAppRoute] = useState<AppRoute>(() => (window.location.pathname === "/student/profile" ? "student_profile" : "dashboard"));
  const [authBootstrapped, setAuthBootstrapped] = useState(false);
  const [studentSnapshot, setStudentSnapshot] = useState(fallbackWorkspaceSnapshot.student);
  const [teacherSnapshot, setTeacherSnapshot] = useState(fallbackWorkspaceSnapshot.teacher);
  const [adminSnapshot, setAdminSnapshot] = useState(fallbackWorkspaceSnapshot.admin);
  const [workspaceRefreshToken, setWorkspaceRefreshToken] = useState(0);
  const workspaces = useMemo(() => (session ? availableWorkspaces(session) : []), [session]);

  useEffect(() => {
    const bodyClass = shellView === "landing"
      ? "fd-body fd-landing-body"
      : shellView === "login"
        ? "fd-body fd-auth-light"
        : "fd-body fd-app";
    document.body.className = bodyClass;
  }, [shellView]);

  useEffect(() => {
    let cancelled = false;

    async function hydrateSession() {
      try {
        const response = await fetch("/api/v2/auth/session", {
          credentials: "include"
        });

        if (!response.ok) {
          if (!cancelled && initialShellView() === "app") {
            setSession(null);
            setWorkspace("student");
            setShellView("login");
            setPath("/login");
          }
          return;
        }

        const payload = await response.json() as { session?: AppSession };

        if (cancelled || !payload.session) {
          return;
        }

        setSession(payload.session);
        setWorkspace(payload.session.workspace);
        if (window.location.pathname === "/student/profile" && payload.session.workspace === "student") {
          setAppRoute("student_profile");
        }

        if (initialShellView() !== "landing") {
          setShellView("app");
          if (window.location.pathname === "/student/profile" && payload.session.workspace === "student") {
            return;
          }
          if (window.location.pathname !== "/dashboard") {
            setPath("/dashboard");
          }
        }
      } catch {
        if (!cancelled && initialShellView() === "app") {
          setShellView("login");
          setPath("/login");
        }
      } finally {
        if (!cancelled) {
          setAuthBootstrapped(true);
        }
      }
    }

    void hydrateSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!session) {
      return undefined;
    }

    if (!workspaces.includes(workspace)) {
      setWorkspace(session.workspace);
      return undefined;
    }

    let cancelled = false;

    async function loadWorkspace() {
      const response = await fetch(`/api/v2/workspaces/${workspace}`, {
        credentials: "include"
      });

      if (!response.ok) {
        throw new Error(`Workspace request failed: ${response.status}`);
      }

      const payload = await response.json();

      if (cancelled) {
        return;
      }

      if (workspace === "student") {
        setStudentSnapshot(payload as StudentWorkspaceSnapshot);
      }

      if (workspace === "teacher") {
        setTeacherSnapshot(payload as TeacherWorkspaceSnapshot);
      }

      if (workspace === "admin") {
        setAdminSnapshot(payload as AdminWorkspaceSnapshot);
      }
    }

    void loadWorkspace().catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [session, workspace, workspaces, workspaceRefreshToken]);

  function refreshWorkspace() {
    setWorkspaceRefreshToken((current) => current + 1);
  }

  function handleLogin(nextSession: AppSession) {
    setSession(nextSession);
    setWorkspace(nextSession.workspace);
    setShellView("app");
    setAppRoute("dashboard");
    setPath("/dashboard");
  }

  function handleLogout() {
    void fetch("/api/v2/auth/logout", {
      credentials: "include",
      method: "POST"
    }).catch(() => undefined);
    setSession(null);
    setWorkspace("student");
    setShellView("landing");
    setPath("/");
  }

  function showLogin() {
    setShellView("login");
    setPath("/login");
  }

  return {
    adminSnapshot,
    appRoute,
    authBootstrapped,
    handleLogin,
    handleLogout,
    session,
    shellView,
    showLogin,
    studentSnapshot,
    teacherSnapshot,
    refreshWorkspace,
    setAppRoute,
    workspace,
    workspaces,
    setWorkspace
  };
}
